import React, { useState, useEffect, Fragment, useMemo } from 'react'
import {
  Avatar,
  Button,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  ListItemIcon,
  ListItemButton
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import DeleteIcon from 'mdi-react/DeleteIcon'
import AddIcon from 'mdi-react/AddIcon'
import EditIcon from 'mdi-react/EditIcon'
import DoneIcon from 'mdi-react/DoneIcon'
import ArrowLeftIcon from 'mdi-react/ArrowLeftIcon'
import {
  SocialMediaPlatform,
  UserFacebookAccounts,
  UserInstagramAccounts,
  InstagramAccount,
  FacebookPage,
  isInstagramAccount,
  UserFacebookAccount
} from '@seesignage/seesignage-utils'
import { useDispatch } from 'react-redux'
import ArrowRightIcon from 'mdi-react/ArrowRightIcon'
import Skeleton from '@mui/material/Skeleton'
import { TFunction } from 'i18next'
import colors from '../../../../../styles/common/colors'
import {
  getFacebookPages,
  getFacebookAccount
} from '../../../../../services/api/SocialMedia/facebook'
import { addSocialMediaAccount, deleteSocialMediaAccount } from '../../../../../actions/users'
import { getConnectedInstagramAccounts } from '../../../../../services/api/SocialMedia/instagram'
import AlertDialog from '../../../../../components/AlertDialog'
import SocialMediaGuidePanel from '../../../../../components/ExtensionPanels/SocialMediaGuidePanel'

const useStyles = makeStyles(() => ({
  list: {
    maxHeight: '60vh',
    overflowY: 'hidden'
  },
  card: {
    width: '240px',
    height: '70px',
    marginTop: '5px',
    borderLeft: `8px solid white`
  },
  selectedCard: {
    borderLeft: `8px solid ${colors.seesignageColor}`
  },
  cardAction: {
    height: '100%',
    width: '100%',
    display: 'flex',
    padding: '0px 10px 0px 10px',
    justifyContent: 'space-between'
  },
  cardText: {
    fontSize: '18px',
    fontWeight: 'bold'
  },
  accountsControls: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between'
  },
  accountSelectorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  instagramAccountsContainer: {
    width: '250px',
    minHeight: '250px',
    background: 'rgba(0, 0, 0, 0.05)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  facebookAccountsContainer: {
    width: '250px',
    height: '250px',
    overflowX: 'hidden',
    overflowY: 'scroll',
    background: 'rgba(0, 0, 0, 0.05)'
  },
  accountsAndPagesWrapper: {
    width: '200%',
    minHeight: '100%',
    display: 'flex',
    transitionDuration: '200ms'
  },
  accountsAndPagesContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  cardContent: {
    display: 'flex',
    alignItems: 'center'
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  editButton: {
    alignSelf: 'end'
  },
  title: {
    fontWeight: 500,
    marginBottom: 10
  },
  requirementsSteps: {
    margin: 0
  }
}))

interface ListItem {
  id: string
  url?: string | undefined
  primaryText: string
  secondaryText?: string | undefined
}
interface ListSocialMediaItemsProps {
  t: TFunction
  loading: boolean
  editMode: boolean
  editable?: boolean
  platform: SocialMediaPlatform
  onEditButtonClick: () => void
  onAddButtonClick: () => void
  onBackButtonClick?: () => void
  onClickItem: (id: string) => void
  items: ListItem[]
}

const ListSocialMediaItems = ({
  loading,
  editMode,
  platform,
  onAddButtonClick,
  onEditButtonClick,
  onBackButtonClick,
  editable,
  onClickItem,
  items,
  t
}: ListSocialMediaItemsProps) => {
  const classes = useStyles()
  const [openDialog, setOpenDialog] = useState(false)
  const [clickedAccount, setClickedAccount] = useState<ListItem | null>(null)
  const handleClickItem = (item: ListItem) => {
    if (editMode) {
      setClickedAccount(item)
      setOpenDialog(true)
    } else {
      onClickItem(item.id)
    }
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setClickedAccount(null)
  }

  return (
    <div className={classes.content}>
      <AlertDialog
        type='delete'
        onConfirm={() => {
          onClickItem(clickedAccount?.id ?? '')
          handleCloseDialog()
        }}
        onCancel={handleCloseDialog}
        open={openDialog}
        title={`${t('contents.widgets.socialMedia.deleteAccountDialogTitle')}: ${
          clickedAccount?.primaryText
        }`}
        content={t('contents.widgets.socialMedia.deleteAccountDialogContent')}
        confirmText={t('contents.widgets.socialMedia.delete')}
        cancelText={t('contents.widgets.socialMedia.cancel')}
      />
      <Grid container justifyContent='center'>
        <Grid container item xs={12} md={10}>
          <Grid item xs={12}>
            <Typography variant='h5' gutterBottom textTransform='capitalize'>
              {platform}
            </Typography>
          </Grid>
          <Grid item xs={12} justifyContent='space-between' container>
            {editable ? (
              <>
                <Button
                  startIcon={editMode ? <DoneIcon /> : <EditIcon />}
                  color='primary'
                  disabled={!editMode && items.length === 0}
                  onClick={onEditButtonClick}>
                  {editMode
                    ? t('contents.widgets.socialMedia.editDone')
                    : t('contents.widgets.socialMedia.edit')}
                </Button>
                <Button
                  startIcon={<AddIcon />}
                  color='primary'
                  disabled={editMode}
                  onClick={onAddButtonClick}>
                  {t('contents.widgets.socialMedia.addAccount')}
                </Button>
              </>
            ) : (
              <Button startIcon={<ArrowLeftIcon />} color='primary' onClick={onBackButtonClick}>
                {t('contents.widgets.socialMedia.account')}
              </Button>
            )}
          </Grid>
          <Grid item xs={12}>
            <List dense classes={{ root: classes.list }}>
              {loading ? (
                [1, 2, 3].map(id => (
                  <Fragment key={id}>
                    <ListItem>
                      <ListItemButton>
                        <ListItemIcon>
                          <Skeleton animation='wave' variant='circular' width={40} height={40} />
                        </ListItemIcon>
                        <ListItemText
                          primary={<Skeleton animation='wave' height={20} />}
                          secondary={<Skeleton animation='wave' height={10} width='80%' />}
                        />
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                  </Fragment>
                ))
              ) : items.length === 0 ? (
                <Typography variant='h6' align='center' color='textSecondary'>
                  {t('contents.widgets.socialMedia.noItemsFound.account')}
                </Typography>
              ) : (
                <>
                  <Divider light />
                  {items.map(item => (
                    <Fragment key={item.id}>
                      <ListItem disablePadding>
                        <ListItemButton onClick={() => handleClickItem(item)}>
                          <ListItemAvatar>
                            {item.url ? (
                              <Avatar src={item.url} />
                            ) : (
                              <Skeleton
                                animation='wave'
                                variant='circular'
                                width={40}
                                height={40}
                              />
                            )}
                          </ListItemAvatar>
                          <ListItemText
                            primary={<Typography variant='h6'>{item.primaryText}</Typography>}
                            secondary={item.secondaryText}
                          />
                          <ListItemSecondaryAction>
                            <IconButton edge='end'>
                              {editMode ? <DeleteIcon color='red' /> : <ArrowRightIcon />}
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItemButton>
                      </ListItem>
                      <Divider light />
                    </Fragment>
                  ))}
                </>
              )}
            </List>
          </Grid>
        </Grid>
      </Grid>
      <Grid container justifyContent='center'>
        <Grid item xs={12} md={10}>
          <SocialMediaGuidePanel t={t} platform={platform} />
        </Grid>
      </Grid>
    </div>
  )
}

enum SocialMediaItemType {
  account = 'Account',
  instagram = 'Instagram',
  page = 'Page'
}
interface SelectAccountProps<
  T extends UserInstagramAccounts | UserFacebookAccounts =
    | UserInstagramAccounts
    | UserFacebookAccounts
> {
  t: TFunction
  platform: SocialMediaPlatform
  connectedAccounts: T
  onGoToNextStep: () => void
  setSelectedAccount: (account: T[keyof T]) => void
  setSelectedSocialMedia: (socialMedia: InstagramAccount | FacebookPage) => void
}

interface InstagramAccountSelector
  extends Omit<SelectAccountProps<UserInstagramAccounts>, 'setSelectedSocialMedia'> {
  setSelectedSocialMedia: (socialMedia: InstagramAccount) => void
}

const InstagramAccountSelector = ({
  t,
  connectedAccounts,
  platform,
  onGoToNextStep,
  setSelectedAccount,
  setSelectedSocialMedia
}: InstagramAccountSelector) => {
  const dispatch = useDispatch()
  const [fbAccount, setFBAccount] = useState<UserFacebookAccount[]>([])
  const [instaAccounts, setInstaAccounts] = useState<InstagramAccount[]>([])
  const [section, setSection] = useState<SocialMediaItemType>(SocialMediaItemType.account)
  const [loading, setLoading] = useState(true)
  const [editMode, setEditMode] = useState(false)

  const handleAddFacebookAccount = () => {
    setLoading(true)
    dispatch(
      addSocialMediaAccount({
        platform,
        stopLoading: () => setLoading(false)
      })
    )
  }

  const handleDeleteAccount = (id: string) => {
    setLoading(true)
    dispatch(
      deleteSocialMediaAccount({
        account: connectedAccounts[id],
        stopLoading: () => setLoading(false)
      })
    )
  }

  const loadInstagramAccounts = (fbAccountId: string) => {
    setSection(SocialMediaItemType.instagram)
    const { accessToken, name } = connectedAccounts[fbAccountId]
    getConnectedInstagramAccounts(accessToken ?? '', fbAccountId, name)
      .then(setInstaAccounts)
      .finally(() => setLoading(false))
  }

  const handleClickItem = async (id: string) => {
    setLoading(true)
    if (editMode) {
      handleDeleteAccount(id)
    } else {
      if (section === SocialMediaItemType.account) {
        setSelectedAccount(connectedAccounts[id])
        loadInstagramAccounts(id)
      } else {
        setSelectedSocialMedia(
          instaAccounts.find(instaAccount => instaAccount.id === id) as InstagramAccount
        )
        onGoToNextStep()
      }
    }
  }

  const listItems = useMemo(() => {
    switch (section) {
      case SocialMediaItemType.account:
        return fbAccount
      case SocialMediaItemType.instagram:
        return instaAccounts
      default:
        return []
    }
  }, [section, fbAccount, instaAccounts])

  useEffect(() => {
    setLoading(true)
    const loadFacebookAcoounts = async () => {
      Promise.all(
        Object.values(connectedAccounts).map(({ id, name, accessToken }) =>
          getFacebookAccount(accessToken as string, id, name)
        )
      )
        .then(setFBAccount)
        .finally(() => setLoading(false))
    }
    loadFacebookAcoounts()
  }, [connectedAccounts])

  const listSocialMediaItemsProps: ListSocialMediaItemsProps = {
    t,
    loading,
    editMode,
    editable: section === SocialMediaItemType.account,
    platform,
    onClickItem: handleClickItem,
    onAddButtonClick: handleAddFacebookAccount,
    onEditButtonClick: () => setEditMode(value => !value),
    onBackButtonClick: () => setSection(SocialMediaItemType.account),
    items: listItems.map((item: UserFacebookAccount | InstagramAccount) => ({
      id: item.id,
      primaryText: item.name ?? '',
      secondaryText: `${section} ID: ${isInstagramAccount(item) ? `@${item.username}` : item.id}`,
      url: item.picture
    }))
  }

  return <ListSocialMediaItems {...listSocialMediaItemsProps} />
}

interface FacebookAccountSelectorProps
  extends Omit<SelectAccountProps<UserFacebookAccounts>, 'setSelectedSocialMedia'> {
  setSelectedSocialMedia: (socialMedia: FacebookPage) => void
}

const FacebookAccountSelector = ({
  t,
  connectedAccounts,
  platform,
  onGoToNextStep,
  setSelectedAccount,
  setSelectedSocialMedia
}: FacebookAccountSelectorProps) => {
  const dispatch = useDispatch()
  const [fbAccounts, setFBAccounts] = useState<UserFacebookAccount[]>([])
  const [fbPages, setFBPages] = useState<FacebookPage[]>([])

  const [section, setSection] = useState<SocialMediaItemType>(SocialMediaItemType.account)
  const [editMode, setEditMode] = useState(false)
  const [loading, setLoading] = useState(true)

  const handleAddFacebookAccount = () => {
    setLoading(true)
    dispatch(
      addSocialMediaAccount({
        platform,
        stopLoading: () => setLoading(false)
      })
    )
  }

  const handleDeleteAccount = (id: string) => {
    setLoading(true)
    dispatch(
      deleteSocialMediaAccount({
        account: connectedAccounts[id],
        stopLoading: () => setLoading(false)
      })
    )
  }

  const loadFacebookAccountPages = (id: string) => {
    setSection(SocialMediaItemType.page)
    getFacebookPages(connectedAccounts[id].accessToken as string)
      .then(setFBPages)
      .finally(() => setLoading(false))
  }

  const handleClickItem = async (id: string) => {
    setLoading(true)
    if (editMode) {
      handleDeleteAccount(id)
    } else {
      if (section === SocialMediaItemType.account) {
        setSelectedAccount(connectedAccounts[id])
        loadFacebookAccountPages(id)
      } else {
        setSelectedSocialMedia(fbPages.find(fbpage => fbpage.id === id) as FacebookPage)
        onGoToNextStep()
      }
    }
  }

  useEffect(() => {
    setLoading(true)
    const loadFacebookAcoounts = async () => {
      Promise.all(
        Object.values(connectedAccounts).map(({ id, name, accessToken }) =>
          getFacebookAccount(accessToken as string, id, name)
        )
      )
        .then(setFBAccounts)
        .finally(() => setLoading(false))
    }
    loadFacebookAcoounts()
  }, [connectedAccounts])

  const listItems = useMemo(() => {
    switch (section) {
      case SocialMediaItemType.account:
        return fbAccounts
      case SocialMediaItemType.page:
        return fbPages
      default:
        return []
    }
  }, [section, fbAccounts, fbPages])

  const listSocialMediaItemsProps: ListSocialMediaItemsProps = {
    t,
    loading,
    editMode,
    editable: section === SocialMediaItemType.account,
    platform,
    onClickItem: handleClickItem,
    onAddButtonClick: handleAddFacebookAccount,
    onEditButtonClick: () => setEditMode(value => !value),
    onBackButtonClick: () => setSection(SocialMediaItemType.account),
    items: listItems.map(item => ({
      id: item.id,
      primaryText: item.name ?? '',
      secondaryText: `${t('contents.widgets.socialMedia.accountId')}: ${item.id}`,
      url: item.picture
    }))
  }

  return <ListSocialMediaItems {...listSocialMediaItemsProps} />
}

const SelectAccount = ({
  t,
  platform,
  connectedAccounts,
  onGoToNextStep,
  setSelectedAccount,
  setSelectedSocialMedia
}: SelectAccountProps) => {
  const renderAccountView = () => {
    switch (platform) {
      case SocialMediaPlatform.facebook: {
        const facebookAccountSelectorProps = {
          t,
          connectedAccounts: connectedAccounts as UserFacebookAccounts,
          onGoToNextStep,
          platform,
          setSelectedAccount,
          setSelectedSocialMedia
        }
        return <FacebookAccountSelector {...facebookAccountSelectorProps} />
      }
      case SocialMediaPlatform.instagram: {
        const instagramAccountSelectorProps = {
          t,
          connectedAccounts: connectedAccounts as UserInstagramAccounts,
          onGoToNextStep,
          platform,
          setSelectedAccount,
          setSelectedSocialMedia
        }
        return <InstagramAccountSelector {...instagramAccountSelectorProps} />
      }

      default:
        return null
    }
  }
  return renderAccountView()
}

export default SelectAccount
